import React, { useState } from "react";

/* Font specimen sheet, compare display + body pairings for the SWITCH redesign.
   Route: /type-specimen                                                        */

const PAIRINGS = [
  {
    id: "editorial",
    name: "A · Editorial Serif",
    vibe: "High-contrast, premium, confident authority",
    display: "'Fraunces', Georgia, serif",
    displayWeight: 900,
    displayStyle: {},
    body: "'Inter', sans-serif",
  },
  {
    id: "grotesk",
    name: "B · Clean Grotesk (bold & tight)",
    vibe: "Modern, Swiss, no-nonsense, edgy but not weird",
    display: "'Space Grotesk', sans-serif",
    displayWeight: 700,
    displayStyle: { letterSpacing: "-0.03em" },
    body: "'Inter', sans-serif",
  },
  {
    id: "impact",
    name: "C · Condensed Impact",
    vibe: "Big, dramatic, magazine-cover energy",
    display: "'Anton', sans-serif",
    displayWeight: 400,
    displayStyle: { letterSpacing: "-0.01em", textTransform: "none" },
    body: "'Inter', sans-serif",
  },
  {
    id: "expanded",
    name: "D · Expanded Grotesk",
    vibe: "Wide, architectural, corporate-brutalist",
    display: "'Archivo', sans-serif",
    displayWeight: 900,
    displayStyle: { letterSpacing: "-0.02em" },
    body: "'IBM Plex Sans', sans-serif",
  },
  {
    id: "mono",
    name: "E · Mono-influenced (technical)",
    vibe: "Precise, systems / engineering feel",
    display: "'Space Mono', monospace",
    displayWeight: 700,
    displayStyle: { letterSpacing: "-0.04em" },
    body: "'IBM Plex Sans', sans-serif",
  },
  {
    id: "current",
    name: "F · Current prototype (Syne)",
    vibe: "What's live now, for reference",
    display: "'Syne', sans-serif",
    displayWeight: 800,
    displayStyle: { letterSpacing: "-0.04em" },
    body: "'Inter', sans-serif",
  },
];

const BODY_SAMPLE =
  "SWITCH is a problem-led growth company. We diagnose what is holding growth back, build the strategy around execution, and stay to make it happen. Not another deck. A system that moves.";

const Specimen = ({ p, dark }) => {
  const fg = dark ? "#F5F2EA" : "#282828";
  const sub = dark ? "rgba(245,242,234,0.55)" : "rgba(40,40,40,0.55)";
  const line = dark ? "rgba(245,242,234,0.14)" : "rgba(40,40,40,0.12)";
  return (
    <div
      data-testid={`specimen-${p.id}`}
      className="px-6 py-14 md:px-12 md:py-20"
      style={{ backgroundColor: dark ? "#282828" : "#F5F2EA", color: fg }}
    >
      <div className="mx-auto w-full max-w-[1100px]">
        <div className="mb-8 flex flex-wrap items-baseline justify-between gap-2 border-b pb-4" style={{ borderColor: line }}>
          <h2
            style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, letterSpacing: "0.12em" }}
            className="text-xs uppercase"
          >
            {p.name}
          </h2>
          <p style={{ fontFamily: "'Inter', sans-serif", color: sub }} className="text-sm">
            {p.vibe}
          </p>
        </div>

        <p style={{ fontFamily: p.body, color: sub }} className="mb-3 text-xs uppercase tracking-widest">
          Working apart
        </p>
        <h1
          style={{
            fontFamily: p.display,
            fontWeight: p.displayWeight,
            lineHeight: 0.95,
            ...p.displayStyle,
          }}
          className="text-5xl sm:text-6xl md:text-7xl"
        >
          Everyone is working.<br />
          Growth <span style={{ fontStyle: p.id === "editorial" ? "italic" : "normal" }}>isn&rsquo;t.</span>
        </h1>

        <p style={{ fontFamily: p.body, color: sub }} className="mb-3 mt-14 text-xs uppercase tracking-widest">
          Working as one
        </p>
        <h1
          style={{
            fontFamily: p.display,
            fontWeight: p.displayWeight,
            lineHeight: 0.95,
            ...p.displayStyle,
          }}
          className="text-5xl sm:text-6xl md:text-7xl"
        >
          Strategy, built<br />
          to be <span style={{ color: "#5f8f10" }}>executed.</span>
        </h1>

        <div className="mt-14 grid gap-8 md:grid-cols-2">
          <div>
            <p style={{ fontFamily: p.display, fontWeight: p.displayWeight, ...p.displayStyle }} className="text-3xl md:text-4xl">
              The SWITCH model
            </p>
            <p style={{ fontFamily: p.body, color: sub }} className="mt-4 text-base leading-relaxed">
              {BODY_SAMPLE}
            </p>
          </div>
          <div>
            <p style={{ fontFamily: p.body, fontWeight: 600 }} className="text-sm uppercase tracking-widest">
              Body / UI · {p.body.split(",")[0].replace(/'/g, "")}
            </p>
            <p style={{ fontFamily: p.body }} className="mt-4 text-base leading-relaxed" >
              Bring us your growth problem. Find your switch. Book a 30-minute conversation.
            </p>
            <div className="mt-6 flex gap-3">
              <span
                style={{ fontFamily: p.body, fontWeight: 600, background: "#282828", color: "#F5F2EA" }}
                className="inline-flex items-center px-5 py-3 text-sm"
              >
                Bring Us Your Growth Problem
              </span>
              <span
                style={{ fontFamily: p.body, fontWeight: 600, background: "#5f8f10", color: "#282828" }}
                className="inline-flex items-center px-5 py-3 text-sm"
              >
                Find Your Switch
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function TypeSpecimen() {
  const [dark, setDark] = useState(false);
  return (
    <div style={{ fontFamily: "'Inter', sans-serif" }}>
      <div
        className="sticky top-0 z-50 flex items-center justify-between border-b px-6 py-4 md:px-12"
        style={{ background: "#282828", color: "#F5F2EA", borderColor: "rgba(245,242,234,0.14)" }}
      >
        <div>
          <p className="text-xs uppercase tracking-[0.16em] opacity-60">SWITCH · Type specimen</p>
          <p className="text-lg font-bold">Pick a display + body pairing</p>
        </div>
        <button
          data-testid="specimen-theme-toggle"
          onClick={() => setDark((d) => !d)}
          className="border px-4 py-2 text-sm font-semibold"
          style={{ borderColor: "rgba(245,242,234,0.3)" }}
        >
          Preview on: {dark ? "Dark" : "Light"}
        </button>
      </div>
      {PAIRINGS.map((p, i) => (
        <Specimen key={p.id} p={p} dark={dark} />
      ))}
      <div className="px-6 py-16 text-center" style={{ background: "#282828", color: "#F5F2EA" }}>
        <p style={{ fontFamily: "'Inter', sans-serif" }} className="mx-auto max-w-md text-sm opacity-70">
          Tell me the letter (A-F) you like, and whether Light or Dark reads best, and I&rsquo;ll apply it to the prototype.
        </p>
      </div>
    </div>
  );
}
